import { supabase } from "@/integrations/supabase/client";
import {
  Collection,
  PaginatedResponse,
  BookSummary,
  PaginationParams,
} from "@/types";

// Function to fetch collections with pagination
export const fetchCollections = async ({
  page = 1,
  pageSize = 10,
  filters = {},
}: {
  page?: number;
  pageSize?: number;
  filters?: Record<string, string | boolean | number>;
} = {}): Promise<PaginatedResponse<Collection>> => {
  try {
    const searchTerm = filters.search
      ? String(filters.search).toLowerCase()
      : "";
    const isSearching = searchTerm.length > 0;

    // Calculate offset for pagination
    const offset = (page - 1) * pageSize;

    // Start building the query for collections
    let query = supabase.from("collections").select("*", { count: "exact" });

    // Apply filters if provided
    if (filters) {
      // Search by title
      if (isSearching) {
        query = query.ilike("title", `%${searchTerm}%`);
      }

      // Filter by active status
      if (filters.active !== undefined) {
        query = query.eq("is_active", filters.active);
      }

      // Sort by criteria
      if (filters.sortBy) {
        switch (filters.sortBy) {
          case "newest":
            query = query.order("created_at", { ascending: false });
            break;
          case "oldest":
            query = query.order("created_at", { ascending: true });
            break;
          case "position":
            query = query.order("position", { ascending: true });
            break;
          case "position_desc":
            query = query.order("position", { ascending: false });
            break;
          case "title":
            query = query.order("title", { ascending: true });
            break;
          case "title_desc":
            query = query.order("title", { ascending: false });
            break;
          case "updated":
            query = query.order("updated_at", { ascending: false });
            break;
          case "createdAt":
            query = query.order("created_at", { ascending: true });
            break;
          case "createdAt_desc":
            query = query.order("created_at", { ascending: false });
            break;
          default:
            query = query.order("position", { ascending: true });
        }
      } else {
        // Default sort by position
        query = query.order("position", { ascending: true });
      }
    }

    // Apply pagination
    const {
      data: collectionsData,
      error,
      count,
    } = await query.range(offset, offset + pageSize - 1);

    if (error) {
      console.error("Error fetching collections:", error);
      throw error;
    }

    if (!collectionsData || collectionsData.length === 0) {
      return {
        data: [],
        total: 0,
        page,
        pageSize,
        totalPages: 0,
      };
    }

    // Get collection IDs
    const collectionIds = collectionsData.map((collection) => collection.id);

    // Fetch books for each collection
    const collectionsWithBooks: Collection[] = await Promise.all(
      collectionsData.map(async (collection) => {
        // Get book IDs from the junction table
        const { data: collectionBooksData, error: collectionBooksError } =
          await supabase
            .from("collections_books")
            .select("book_id, position")
            .eq("collection_id", collection.id)
            .order("position");

        if (collectionBooksError) {
          console.error(
            "Error fetching books for collection:",
            collectionBooksError
          );
          return {
            ...collection,
            books: [],
            isActive: collection.is_active,
            createdAt: collection.created_at,
            updatedAt: collection.updated_at,
          };
        }

        if (!collectionBooksData || collectionBooksData.length === 0) {
          return {
            ...collection,
            books: [],
            isActive: collection.is_active,
            createdAt: collection.created_at,
            updatedAt: collection.updated_at,
          };
        }

        // Extract book IDs from the junction data
        const bookIds = collectionBooksData.map((cb) => cb.book_id);

        // Fetch basic book data for the collection
        const { data: booksData, error: booksError } = await supabase
          .from("books")
          .select(
            "id, title, cover_image, rating, view_count, created_at, updated_at, is_active"
          )
          .in("id", bookIds);

        if (booksError) {
          console.error("Error fetching books for collection:", booksError);
          return {
            ...collection,
            books: [],
            isActive: collection.is_active,
            createdAt: collection.created_at,
            updatedAt: collection.updated_at,
          };
        }

        // Map the raw database data to our BookSummary type (partial)
        const books = booksData.map((book) => {
          // Find the position for this book in the collection
          const collectionBook = collectionBooksData.find(
            (cb) => cb.book_id === book.id
          );

          return {
            id: book.id,
            title: book.title,
            coverImage: book.cover_image,
            rating: book.rating,
            viewCount: book.view_count,
            createdAt: book.created_at,
            updatedAt: book.updated_at,
            isActive: book.is_active,
            authors: [], // These would be populated in a detailed view
            categories: [],
            readingTime: 0,
            summaryContents: [],
            position: collectionBook?.position || 0,
          };
        });

        // Sort books by their position in the collection
        books.sort((a, b) => a.position - b.position);

        return {
          id: collection.id,
          title: collection.title,
          subtitle: collection.subtitle || undefined,
          coverImage: collection.cover_image || undefined,
          description: collection.description || undefined,
          position: collection.position,
          isActive: collection.is_active,
          books: books,
          createdAt: collection.created_at,
          updatedAt: collection.updated_at,
        };
      })
    );

    return {
      data: collectionsWithBooks,
      total: count || collectionsWithBooks.length,
      page,
      pageSize,
      totalPages: Math.ceil((count || collectionsWithBooks.length) / pageSize),
    };
  } catch (error) {
    console.error("Error in fetchCollections:", error);
    throw error;
  }
};

// Function to fetch a single collection by ID
export const fetchCollectionById = async (
  id: number
): Promise<Collection | null> => {
  try {
    // Fetch the collection
    const { data: collection, error } = await supabase
      .from("collections")
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      console.error("Error fetching collection:", error);
      throw error;
    }

    if (!collection) {
      return null;
    }

    // Fetch books for the collection with ordering
    const { data: collectionBooksData, error: collectionBooksError } =
      await supabase
        .from("collections_books")
        .select("book_id, position")
        .eq("collection_id", id)
        .order("position");

    if (collectionBooksError) {
      console.error(
        "Error fetching books for collection:",
        collectionBooksError
      );
      return {
        ...collection,
        books: [],
        isActive: collection.is_active,
        createdAt: collection.created_at,
        updatedAt: collection.updated_at,
      };
    }

    if (!collectionBooksData || collectionBooksData.length === 0) {
      return {
        ...collection,
        books: [],
        isActive: collection.is_active,
        createdAt: collection.created_at,
        updatedAt: collection.updated_at,
      };
    }

    // Extract book IDs
    const bookIds = collectionBooksData.map((cb) => cb.book_id);

    // Fetch detailed book information
    const { data: booksData, error: booksError } = await supabase
      .from("books")
      .select(
        "id, title, cover_image, rating, view_count, created_at, updated_at, is_active"
      )
      .in("id", bookIds);

    if (booksError) {
      console.error("Error fetching books for collection:", booksError);
      return {
        ...collection,
        books: [],
        isActive: collection.is_active,
        createdAt: collection.created_at,
        updatedAt: collection.updated_at,
      };
    }

    // Fetch additional book data - we'll need to fetch authors and categories
    // Authors
    const { data: authorsData } = await supabase
      .from("books_authors")
      .select(
        `
        book_id,
        people!books_authors_person_id_fkey(
          id, 
          name,
          created_at,
          updated_at
        )
      `
      )
      .in("book_id", bookIds);

    // Categories
    const { data: categoriesData } = await supabase
      .from("books_categories")
      .select(
        `
        book_id,
        categories!books_categories_category_id_fkey(
          id, 
          name,
          is_active
        )
      `
      )
      .in("book_id", bookIds);

    // Map the data to our BookSummary type
    const books = booksData.map((book) => {
      // Find the book's position in the collection
      const collectionBook = collectionBooksData.find(
        (cb) => cb.book_id === book.id
      );

      // Find authors for this book
      const authors = authorsData
        ? authorsData
            .filter((a) => a.book_id === book.id)
            .map((a) => ({
              id: a.people.id,
              name: a.people.name,
              roles: [], // This would need additional data
              createdAt: a.people.created_at,
              updatedAt: a.people.updated_at,
            }))
        : [];

      // Find categories for this book
      const categories = categoriesData
        ? categoriesData
            .filter((c) => c.book_id === book.id)
            .map((c) => ({
              id: c.categories.id,
              name: c.categories.name,
              is_active: c.categories.is_active,
              isActive: c.categories.is_active,
            }))
        : [];

      return {
        id: book.id,
        title: book.title,
        coverImage: book.cover_image,
        rating: book.rating,
        viewCount: book.view_count,
        createdAt: book.created_at,
        updatedAt: book.updated_at,
        isActive: book.is_active,
        authors,
        categories,
        readingTime: 0, // Would need additional data
        summaryContents: [], // Would need additional data
        position: collectionBook?.position || 0,
      };
    });

    // Sort books by their position in the collection
    books.sort((a, b) => a.position - b.position);

    return {
      id: collection.id,
      title: collection.title,
      subtitle: collection.subtitle || undefined,
      coverImage: collection.cover_image || undefined,
      description: collection.description || undefined,
      position: collection.position,
      isActive: collection.is_active,
      books: books,
      createdAt: collection.created_at,
      updatedAt: collection.updated_at,
    };
  } catch (error) {
    console.error("Error in fetchCollectionById:", error);
    throw error;
  }
};

// Function to create or update a collection
export const saveCollection = async (
  collection: Partial<Collection>
): Promise<{ id: number } | null> => {
  try {
    if (collection.id) {
      // Update existing collection
      const { data, error } = await supabase
        .from("collections")
        .update({
          title: collection.title,
          subtitle: collection.subtitle,
          cover_image: collection.coverImage,
          description: collection.description,
          position: collection.position,
          is_active: collection.isActive,
          updated_at: new Date().toISOString(),
        })
        .eq("id", collection.id)
        .select("id")
        .single();

      if (error) {
        console.error("Error updating collection:", error);
        throw error;
      }

      return data;
    } else {
      // Create new collection
      const { data, error } = await supabase
        .from("collections")
        .insert({
          title: collection.title,
          subtitle: collection.subtitle,
          cover_image: collection.coverImage,
          description: collection.description,
          position: collection.position || 0,
          is_active:
            collection.isActive !== undefined ? collection.isActive : true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select("id")
        .single();

      if (error) {
        console.error("Error creating collection:", error);
        throw error;
      }

      return data;
    }
  } catch (error) {
    console.error("Error in saveCollection:", error);
    throw error;
  }
};

// Function to delete a collection
export const deleteCollection = async (
  collectionId: number
): Promise<boolean> => {
  try {
    // Delete the collection
    const { error } = await supabase
      .from("collections")
      .delete()
      .eq("id", collectionId);

    if (error) {
      console.error("Error deleting collection:", error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error("Error in deleteCollection:", error);
    throw error;
  }
};

// Function to add a book to a collection
export const addBookToCollection = async (
  collectionId: number,
  bookId: number,
  position?: number
): Promise<boolean> => {
  try {
    // Check if the book is already in the collection
    const { data: existingData, error: checkError } = await supabase
      .from("collections_books")
      .select("*")
      .eq("collection_id", collectionId)
      .eq("book_id", bookId)
      .single();

    if (checkError && checkError.code !== "PGRST116") {
      // PGRST116 is the error code for "no rows returned" which is expected
      console.error("Error checking if book exists in collection:", checkError);
      throw checkError;
    }

    if (existingData) {
      // Book already exists in the collection, update its position if provided
      if (position !== undefined) {
        const { error: updateError } = await supabase
          .from("collections_books")
          .update({ position })
          .eq("collection_id", collectionId)
          .eq("book_id", bookId);

        if (updateError) {
          console.error(
            "Error updating book position in collection:",
            updateError
          );
          throw updateError;
        }
      }
    } else {
      // If position is not provided, fetch the current max position and add 1
      let positionToUse = position;
      if (positionToUse === undefined) {
        const { data: maxPositionData, error: maxPositionError } =
          await supabase
            .from("collections_books")
            .select("position")
            .eq("collection_id", collectionId)
            .order("position", { ascending: false })
            .limit(1)
            .single();

        if (maxPositionError && maxPositionError.code !== "PGRST116") {
          console.error("Error fetching max position:", maxPositionError);
          throw maxPositionError;
        }

        positionToUse = maxPositionData ? maxPositionData.position + 1 : 0;
      }

      // Add the book to the collection
      const { error } = await supabase.from("collections_books").insert({
        collection_id: collectionId,
        book_id: bookId,
        position: positionToUse,
        created_at: new Date().toISOString(),
      });

      if (error) {
        console.error("Error adding book to collection:", error);
        throw error;
      }
    }

    return true;
  } catch (error) {
    console.error("Error in addBookToCollection:", error);
    throw error;
  }
};

// Function to remove a book from a collection
export const removeBookFromCollection = async (
  collectionId: number,
  bookId: number
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from("collections_books")
      .delete()
      .eq("collection_id", collectionId)
      .eq("book_id", bookId);

    if (error) {
      console.error("Error removing book from collection:", error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error("Error in removeBookFromCollection:", error);
    throw error;
  }
};

// Function to update book positions in a collection
export const updateCollectionBookPositions = async (
  collectionId: number,
  bookPositions: { bookId: number; position: number }[]
): Promise<boolean> => {
  try {
    // Use a transaction to update all positions
    const updates = bookPositions.map(({ bookId, position }) => {
      return supabase
        .from("collections_books")
        .update({ position })
        .eq("collection_id", collectionId)
        .eq("book_id", bookId);
    });

    // Execute all updates
    await Promise.all(updates);

    return true;
  } catch (error) {
    console.error("Error in updateCollectionBookPositions:", error);
    throw error;
  }
};
